/**
 * Modal Provider - Global modal management
 * Provides alert() and confirm() replacements via React Context
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { AlertModal } from './AlertModal'
import { ConfirmModal } from './ConfirmModal'

interface AlertOptions {
  title?: string
  message: string
  type?: 'info' | 'warning' | 'error' | 'success'
}

interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  type?: 'info' | 'warning' | 'danger'
}

interface ModalContextValue {
  alert: (options: AlertOptions) => Promise<void>
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ModalContext = createContext<ModalContextValue | null>(null)

export function ModalProvider({ children }: { children: ReactNode }) {
  const [alertState, setAlertState] = useState<AlertOptions & { isOpen: boolean }>({
    isOpen: false,
    message: '',
  })

  const [confirmState, setConfirmState] = useState<ConfirmOptions & { isOpen: boolean }>({
    isOpen: false,
    message: '',
  })

  const [alertResolver, setAlertResolver] = useState<(() => void) | null>(null)
  const [confirmResolver, setConfirmResolver] = useState<((value: boolean) => void) | null>(null)

  const alert = useCallback((options: AlertOptions): Promise<void> => {
    return new Promise((resolve) => {
      setAlertState({
        ...options,
        isOpen: true,
      })
      setAlertResolver(() => resolve)
    })
  }, [])

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({
        ...options,
        isOpen: true,
      })
      setConfirmResolver(() => resolve)
    })
  }, [])

  const handleAlertClose = useCallback(() => {
    setAlertState((prev) => ({ ...prev, isOpen: false }))
    if (alertResolver) {
      alertResolver()
      setAlertResolver(null)
    }
  }, [alertResolver])

  const handleConfirmConfirm = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }))
    if (confirmResolver) {
      confirmResolver(true)
      setConfirmResolver(null)
    }
  }, [confirmResolver])

  const handleConfirmCancel = useCallback(() => {
    setConfirmState((prev) => ({ ...prev, isOpen: false }))
    if (confirmResolver) {
      confirmResolver(false)
      setConfirmResolver(null)
    }
  }, [confirmResolver])

  return (
    <ModalContext.Provider value={{ alert, confirm }}>
      {children}

      <AlertModal
        isOpen={alertState.isOpen}
        onClose={handleAlertClose}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
      />

      <ConfirmModal
        isOpen={confirmState.isOpen}
        onConfirm={handleConfirmConfirm}
        onCancel={handleConfirmCancel}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        type={confirmState.type}
      />
    </ModalContext.Provider>
  )
}

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}
